import React, { useState, useEffect, useCallback } from "react";

// recoil
import { useRecoilValue, useSetRecoilState } from "recoil";
import { userCredentials, routeInfo } from "recoilState/GlobalState";

//Services
import { accesoGet, zonaGet, eventoGet } from "services/services-mia/miakey";

// utils
import { useFormik } from "formik";
import XGridServer from "components/UI/Grid/XGridServer";
import { Buttons, HeaderButton } from "components";
import { TableView, FilterAltOffRounded, DescriptionOutlined } from "@mui/icons-material";
import * as XLSX from "xlsx";
import Filters from "components/Filters";

// consts
import { eventos as titles } from "consts/titulos-de-tablas";
import { Routes } from "consts/Routes-App";

// styles
import s from "styles/pages/Pages.module.scss"

import { useService } from "components/route/ServiceRoute";
import { transformDate } from "services/Local-services";
import moment from "moment";

const FECHA_HOY = moment().format("YYYY-MM-DD").toString();

/**
 * Normaliza el documento ingresado: deja solo letras y números (quita puntos,
 * guiones y espacios, ej. "12.345.678" → "12345678"). Devuelve null si queda vacío.
 * @param {string} value
 * @returns {string|null}
 */
const sanitizeDocumento = (value) => {
    const clean = String(value ?? "").replace(/[^a-zA-Z0-9]/g, "");
    return clean || null;
};

export default function Eventos() {
    const [forceUpload, setForceUpload] = useState(false);
    const [zonas, setZonas] = useState([]);
    const [accesos, setAccesos] = useState([]);
    const [accesosFiltrado, setAccesosFiltrado] = useState([]);

    const { service } = useService();

    const credentials = useRecoilValue(userCredentials);
    const setRoute = useSetRecoilState(routeInfo);

    const updateList = useCallback(() => {
        setForceUpload((value) => !value);
    }, []);

    const statusCodeLabel = (code) => {
        if (code === "A") return "E/S";
        if (code === "E") return "Entrada";
        if (code === "S") return "Salida";
        return code;
    };

    const getEventos = async (page, pageSize) => {
        const { result, total } = await eventoGet({
            fechaDesde: formik.values.fechaDesde ?? FECHA_HOY,
            fechaHasta: formik.values.fechaHasta ?? FECHA_HOY,
            gateZonaId: formik.values.zona || null,
            gateLugarId: formik.values.acceso || null,
            usuarioNombres: formik.values.usuario || null,
            usuarioNroDeDocumentoX: sanitizeDocumento(formik.values.documento),
            page: page + 1,
            pageSize,
        }, credentials);

        const newRows = result.map((item) => {
            const {
                gateESHistoFH,
                gateESHistoMetodo,
                gateESHistoId,
                gateESHistoStatusCode,
                gateLugarId,
                gateLugarNombre,
                gateZonaId,
                gateZonaNombre,
                usuarioApellidos,
                usuarioId,
                usuarioNombres,
                usuarioNroDeDocumentoX,
            } = item;

            return {
                id: gateESHistoId,
                gateESHistoFH: String(gateESHistoFH),
                gateESHistoId,
                gateESHistoStatusCode,
                gateLugarId,
                gateLugarNombre,
                gateZonaId,
                gateZonaNombre,
                usuarioApellidos,
                usuarioId,
                usuarioNombres,
                usuarioNroDeDocumentoX,
                [titles[0].field]: `${usuarioNombres} ${usuarioApellidos}`,
                [titles[1].field]: usuarioNroDeDocumentoX,
                [titles[2].field]: statusCodeLabel(gateESHistoStatusCode),
                [titles[3].field]: `${transformDate(gateESHistoFH.substring(0, 10))} ${gateESHistoFH.substring(11, 19)} `,
                [titles[4].field]: gateLugarNombre,
                [titles[5].field]: gateZonaNombre,
            };
        });

        return {
            rows: newRows,
            rowCount: total,
        };
    };

    const formik = useFormik({
        initialValues: {
            usuario: "",
            documento: "",
            fechaDesde: FECHA_HOY,
            fechaHasta: FECHA_HOY,
            zona: "",
            acceso: "",
        },
        onSubmit: (values) => {
            updateList(values);
        },
    });

    useEffect(() => {
        setRoute(Routes.eventos)
    }, [setRoute])

    useEffect(() => {
        if (Object.keys(credentials).length > 0 && service?.servicioCod) {
            (async () => {
                const { result: resultZonas } = await zonaGet({}, credentials);
                const { result: resultAccesos } = await accesoGet({}, credentials);
                setZonas(resultZonas);
                setAccesos(resultAccesos);
                setAccesosFiltrado([...resultAccesos]);
                updateList();
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [credentials, service])

    useEffect(() => {
        const zonaId = Number(formik.values.zona);
        formik.setFieldValue("acceso", "");
        if (zonaId) {
            setAccesosFiltrado(accesos.filter(a => a.gateZonaId === zonaId));
        } else {
            setAccesosFiltrado([...accesos]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.zona]);

    const resetFilters = () => {
        formik.resetForm();
        setAccesosFiltrado([...accesos]);
        updateList();
    };

    const CSV_HEADERS = [
        "tipoDeDocumentoCod",
        "tipoDeDocumentoNombre",
        "usuarioNroDeDocumentoX",
        "usuarioGenero",
        "usuarioFechaDeNacimiento",
        "usuarioApellidos",
        "usuarioNombres",
        "zonaCod",
        "zonaNombre",
        "lugarCod",
        "lugarNombre",
        "accion",
        "timestamp",
    ];

    const fetchEventosForExport = async () => {
        const allEventos = await eventoGet({
            fechaDesde: formik.values.fechaDesde ?? FECHA_HOY,
            fechaHasta: formik.values.fechaHasta ?? FECHA_HOY,
            gateZonaId: formik.values.zona || null,
            gateLugarId: formik.values.acceso || null,
            usuarioNombres: formik.values.usuario || null,
            usuarioNroDeDocumentoX: sanitizeDocumento(formik.values.documento),
        }, credentials);

        return allEventos.map((item) => ({
            tipoDeDocumentoCod: item.tipoDeDocumentoCod,
            tipoDeDocumentoNombre: item.tipoDeDocumentoNombre,
            usuarioNroDeDocumentoX: item.usuarioNroDeDocumentoX,
            usuarioGenero: item.usuarioSexo,
            usuarioFechaDeNacimiento: moment(item.usuarioFechaDeNacimiento).format("DD/MM/YYYY"),
            usuarioApellidos: item.usuarioApellidos,
            usuarioNombres: item.usuarioNombres,
            zonaCod: item.gateZonaCod,
            zonaNombre: item.gateZonaNombre,
            lugarCod: item.gateLugarCod,
            lugarNombre: item.gateLugarNombre,
            accion: statusCodeLabel(item.gateESHistoStatusCode),
            timestamp: moment(item.gateESHistoFH).format("DD/MM/YYYY HH:mm:ss"),
        }));
    };

    const exportToCSV = async () => {
        const rowsToExport = await fetchEventosForExport();
        const headers = CSV_HEADERS.join(",");
        const csvRows = rowsToExport.map(row =>
            CSV_HEADERS.map(col => `"${String(row[col] ?? '').replace(/"/g, '""')}"`).join(",")
        );
        const csv = [headers, ...csvRows].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `eventos_${formik.values.fechaDesde}_${formik.values.fechaHasta}.csv`);
        link.click();
        URL.revokeObjectURL(url);
    };

    const exportToXLS = async () => {
        const rowsToExport = await fetchEventosForExport();
        const worksheet = XLSX.utils.json_to_sheet(rowsToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Eventos");
        XLSX.writeFile(workbook, `eventos_${formik.values.fechaDesde}_${formik.values.fechaHasta}.xlsx`, { compression: true });
    };

    return (
        <main className={s.main}>
            <div className={s["filters-actions"]}>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <Filters
                        formik={formik}
                        inputsRender={[
                            { type: "text", name: "usuario", label: "Apellido", disabled: false },
                            { type: "text", name: "documento", label: "Documento", disabled: false },
                            {
                                type: "autocomplete",
                                name: "zona",
                                label: "Zona",
                                width: "225px",
                                disabled: false,
                                options: [{ gateZonaId: "", gateZonaNombre: "Todos" }, ...zonas],
                                firstOptionKey: "gateZonaId",
                                firstOptionName: "gateZonaNombre",
                                firstOptionValue: "gateZonaId",
                            },
                            {
                                type: "autocomplete",
                                name: "acceso",
                                label: "Acceso",
                                width: "225px",
                                disabled: !accesosFiltrado.length,
                                options: [{ gateLugarId: "", gateLugarNombre: "Todos" }, ...accesosFiltrado],
                                firstOptionKey: "gateLugarId",
                                firstOptionName: "gateLugarNombre",
                                firstOptionValue: "gateLugarId",
                            },

                            { type: "date", name: "fechaDesde", label: "Fecha Desde", disabled: false },
                            { type: "date", name: "fechaHasta", label: "Fecha Hasta", disabled: false },
                        ]}
                    />
                    <HeaderButton text={"Limpiar filtros"} onClick={resetFilters}>
                        <FilterAltOffRounded />
                    </HeaderButton>
                </div>
                <Buttons>
                    <HeaderButton
                        text={"Exportar CSV"}
                        onClick={exportToCSV}
                        disabled={false}
                    >
                        <DescriptionOutlined />
                    </HeaderButton>
                    <HeaderButton
                        text={"Exportar XLS"}
                        onClick={exportToXLS}
                        disabled={false}
                    >
                        <TableView />
                    </HeaderButton>
                </Buttons>
            </div>
            <div className={s.container}>
                <XGridServer
                    titles={titles}
                    updateList={getEventos}
                    forceUpload={forceUpload}
                />
            </div>
        </main>
    );
}
